import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useGoverningEventsClient } from "@/lib/api";

// ─── Types ──────────────────────────────────────────────────────────────────────
// Duplicated intentionally: the frontend is a standalone vite workspace with no dependency on
// packages/shared — same convention as Phase0Panel / MeetingTimeline. Mirrors
// SPEC-GOVERNING-EVENTS §3 and SPEC-UNMATCHED-UI §5.4.

export type GoverningGate = 0 | 1 | 2 | 3 | 4;

/** Server-resolved evidence preview for the current blocking gate (CR 2026-09-09). */
export interface GateEvidenceHint {
  meeting_id: string | null;
  meeting_name: string | null;
  meeting_date: string | null;
  avoma_link: string | null;
  cia_result_id: number | null;
}

/** Per-gate state for the whole project (0..4), whether approved or not. */
export interface GateState {
  gate: GoverningGate;
  event_type: string;
  label: string;
  approved: boolean;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  metadata: Record<string, unknown> | null;
  evidence_hint: GateEvidenceHint | null;
}

export interface GoverningEventsResponse {
  project_id: string;
  gates: GateState[];
}

export interface ConfirmGoverningEventInput {
  gate: number;
  event_type: string;
  metadata?: Record<string, unknown>;
}

/** One project_gate_criteria row joined to its gate_exit_criteria text (CR 2026-09-09). */
export interface GateCriterion {
  criterion_id: string;
  phase: string;
  criterion_text: string;
  order_index: number;
  is_checked: boolean;
  checked_by: string | null;
  checked_at: string | null;
}

export interface GateCriteriaResponse {
  project_id: string;
  phases: { phase: string; criteria: GateCriterion[] }[];
}

// ─── Governing events (Gate 0 kickoff) ───────────────────────────────────────────

/**
 * Governing events for a project (SPEC-GOVERNING-EVENTS §6.4).
 * `events` is the always-length-5 gate-state list (0..4). `confirmEvent` records one approval.
 */
/**
 * Every query whose payload embeds the project's current phase / gate state.
 *
 * `current_phase` is never stored — get-project, list-projects and get-project-risk all DERIVE it
 * from governing_events at read time. Confirming a gate therefore stales all of them, not just the
 * governing-events list. Invalidating only ["governing-events", id] left the header badge and
 * PhaseStepper (["project", id]) on the old phase until a hard refresh, because the global
 * staleTime is 5 min with refetchOnWindowFocus off.
 */
export const PHASE_DERIVED_QUERY_KEYS = (
  projectId: string,
): readonly unknown[][] => [
  ["governing-events", projectId],
  ["project", projectId],
  ["project-risk", projectId],
  ["gate-criteria", projectId],
  ["deviation-signals", projectId],
  ["projects"], // prefix — list/dashboard phase column for every filter combination
];

export function invalidatePhaseDerivedQueries(
  queryClient: QueryClient,
  projectId: string,
): Promise<void> {
  return Promise.all(
    PHASE_DERIVED_QUERY_KEYS(projectId).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  ).then(() => undefined);
}

export function useGoverningEvents(projectId: string) {
  const client = useGoverningEventsClient();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["governing-events", projectId],
    queryFn: async (): Promise<GoverningEventsResponse> => {
      const response = await client.get<GoverningEventsResponse>(
        `/api/projects/${projectId}/governing-events`,
      );
      return response.data;
    },
    enabled: !!projectId,
  });

  const confirmEvent = useMutation({
    mutationFn: async (input: ConfirmGoverningEventInput): Promise<void> => {
      await client.post(`/api/projects/${projectId}/governing-events`, input);
    },
    onSuccess: (_data, input) => {
      toast.success(`Gate ${input.gate} confirmed`);
    },
    // onSettled, not onSuccess: on error (e.g. 409 already-approved, or a client timeout on a write
    // that DID commit) the server state still moved, so every view must reconcile either way.
    onSettled: () => invalidatePhaseDerivedQueries(queryClient, projectId),
  });

  return {
    events: query.data?.gates ?? [],
    confirmEvent,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

// ─── Gate exit-criteria status (read-only checklist) ──────────────────────────────

/**
 * Read-only gate exit-criteria for a project (CR 2026-09-09). Returns criteria grouped by phase
 * (project_gate_criteria joined to gate_exit_criteria). CIA-auto-checked rows carry
 * checked_by === 'system:cia'. `criteriaForPhase('Phase N')` selects the current gate's list.
 */
export function useGateCriteriaStatus(projectId: string) {
  const client = useGoverningEventsClient();

  const query = useQuery({
    queryKey: ["gate-criteria", projectId],
    queryFn: async (): Promise<GateCriteriaResponse> => {
      const response = await client.get<GateCriteriaResponse>(
        `/api/projects/${projectId}/gate-criteria`,
      );
      return response.data;
    },
    enabled: !!projectId,
  });

  const criteriaForPhase = (phase: string): GateCriterion[] =>
    query.data?.phases.find((p) => p.phase === phase)?.criteria ?? [];

  return {
    criteriaForPhase,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
